import React from 'react';
import { Box, ButtonBase, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { cardSx, colors, typography } from '../constants';
import { errorMessage } from '../lib/errorMessage';
import { dateRangeLabel } from '../lib/format';
import { buildInsightTopics } from '../lib/insightTopics';
import { useAnalytics } from '../hooks/useProfitData';
import { useDateRange } from '../hooks/useSelection';
import useProfitIqTabs from '../hooks/useProfitIqTabs';
import InsightBlocks from '../components/InsightBlocks';
import { useScreenLoading } from '../components/ScreenLoading';
import { ErrorView, RefreshingOverlay } from '../components/States';

/**
 * Plain-language answers over the same dashboard the other tabs chart.
 *
 * Three levels deep — topics, that topic's five questions, one answer — with
 * the position held in the section context rather than local state so an open
 * answer survives a date change or a trip to another tab and back.
 *
 * Unlike the other tabs this does not go through `ScreenState`: the topic
 * builder writes its own copy for a business with nothing imported, which reads
 * better than a generic empty panel.
 */
export default function Insights() {
  const { range, customLabel, month } = useDateRange();
  const { data, isFetching, error } = useAnalytics();
  const { insightTopic, insightQ, setInsightTopic, setInsightQ } =
    useProfitIqTabs();

  const dateLabel = dateRangeLabel(range, month, customLabel);

  const showLoader = isFetching && !data;
  useScreenLoading('insights', showLoader ? 'Loading insights…' : null);

  if (showLoader) return null;
  if (error && !data) return <ErrorView message={errorMessage(error)} />;
  if (!data) return <ErrorView message="No data available" />;

  return (
    <Box sx={{ position: 'relative', pb: 4 }}>
      {/* As on Overview, an open insight question survives the refetch. */}
      <RefreshingOverlay visible={isFetching && !!data} />
      <InsightsBody
        topics={buildInsightTopics(data)}
        dateLabel={dateLabel}
        insightTopic={insightTopic}
        insightQ={insightQ}
        setInsightTopic={setInsightTopic}
        setInsightQ={setInsightQ}
      />
    </Box>
  );
}

const eyebrowSx = {
  fontSize: typography.caption,
  fontWeight: 700,
  color: colors.faint,
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  mb: 1,
};

function BackLink({ label, onClick }) {
  return (
    <ButtonBase
      onClick={onClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        mb: 1.75,
        borderRadius: 1,
        px: 0.5,
        py: 0.25,
      }}
    >
      <ArrowBackIcon sx={{ fontSize: 16, color: colors.primary }} />
      <Typography
        sx={{
          fontSize: typography.small,
          fontWeight: 600,
          color: colors.primary,
        }}
      >
        {label}
      </Typography>
    </ButtonBase>
  );
}

function InsightsBody({
  topics,
  dateLabel,
  insightTopic,
  insightQ,
  setInsightTopic,
  setInsightQ,
}) {
  // Level 3: an answer is open.
  if (insightTopic && insightQ !== null) {
    const topic = topics[insightTopic];
    const item = topic.qs[insightQ];
    const TopicIcon = topic.icon;

    return (
      <Box sx={{ maxWidth: 860 }}>
        <BackLink
          label={`Back to ${topic.tag} questions`}
          onClick={() => setInsightQ(null)}
        />

        <Box
          sx={{
            backgroundColor: `${topic.color}18`,
            borderRadius: 3,
            py: 1.25,
            px: 1.75,
            mb: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <TopicIcon sx={{ fontSize: 17, color: topic.color }} />
          <Typography
            sx={{
              flex: 1,
              fontSize: typography.caption,
              fontWeight: 700,
              color: topic.color,
            }}
          >
            {item.q}
          </Typography>
        </Box>

        {item.a.text ? (
          <Box sx={{ ...cardSx, p: 2.25, mb: 2.25 }}>
            <Typography
              sx={{
                fontSize: typography.small,
                lineHeight: 1.7,
                color: colors.foreground,
              }}
            >
              {item.a.text}
            </Typography>
          </Box>
        ) : null}

        {item.a.blocks.length > 0 ? (
          <Box sx={{ mb: 0.75 }}>
            <InsightBlocks
              blocks={item.a.blocks}
              accentColor={topic.color}
            />
          </Box>
        ) : null}

        {item.a.followups.length > 0 ? (
          <Box>
            <Typography sx={eyebrowSx}>Continue exploring</Typography>
            {item.a.followups.map((f, i) => (
              <ButtonBase
                key={i}
                onClick={() => {
                  // A follow-up either moves within the topic or jumps to
                  // another one, in which case `target` may be null and the
                  // question list opens instead of an answer.
                  if (f.goto) {
                    setInsightTopic(f.goto);
                    setInsightQ(f.target != null ? f.target : null);
                  } else {
                    setInsightQ(f.target ?? null);
                  }
                }}
                sx={{
                  ...cardSx,
                  width: '100%',
                  py: 1.625,
                  px: 1.875,
                  mb: 1,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  textAlign: 'left',
                  '&:hover': { backgroundColor: colors.muted },
                }}
              >
                <Typography
                  sx={{
                    flex: 1,
                    fontSize: typography.small,
                    fontWeight: 600,
                    color: colors.foreground,
                  }}
                >
                  {f.label}
                </Typography>
                <Typography sx={{ color: colors.primary }}>→</Typography>
              </ButtonBase>
            ))}
          </Box>
        ) : null}
      </Box>
    );
  }

  // Level 2: a topic is open — show its five questions.
  if (insightTopic) {
    const topic = topics[insightTopic];
    const TopicIcon = topic.icon;

    return (
      <Box sx={{ maxWidth: 860 }}>
        <BackLink
          label="Back to all insights"
          onClick={() => setInsightTopic(null)}
        />

        <Box
          sx={{
            backgroundColor: topic.color,
            borderRadius: 3,
            p: 2.25,
            mb: 2,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              mb: 0.75,
            }}
          >
            <TopicIcon sx={{ fontSize: 15, color: '#fff' }} />
            <Typography
              sx={{
                fontSize: typography.caption,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: 1,
                color: '#fff',
                opacity: 0.9,
              }}
            >
              {topic.tag}
            </Typography>
          </Box>
          <Typography
            sx={{
              fontSize: typography.heading,
              fontWeight: 800,
              color: '#fff',
              mb: 0.5,
            }}
          >
            {topic.title}
          </Typography>
          <Typography
            sx={{
              fontSize: typography.caption,
              color: '#fff',
              opacity: 0.92,
            }}
          >
            {topic.summary}
          </Typography>
        </Box>

        <Typography sx={eyebrowSx}>Pick a question to explore</Typography>
        {topic.qs.map((item, i) => (
          <ButtonBase
            key={i}
            onClick={() => setInsightQ(i)}
            sx={{
              ...cardSx,
              width: '100%',
              p: 1.875,
              mb: 1,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 1.25,
              textAlign: 'left',
              '&:hover': { backgroundColor: colors.muted },
            }}
          >
            <Typography
              sx={{
                flex: 1,
                fontSize: typography.small,
                fontWeight: 600,
                color: colors.foreground,
                lineHeight: 1.45,
              }}
            >
              {item.q}
            </Typography>
            <Typography
              sx={{ color: topic.color, fontSize: typography.heading }}
            >
              →
            </Typography>
          </ButtonBase>
        ))}
      </Box>
    );
  }

  // Level 1: every topic as a clickable card.
  return (
    <Box sx={{ maxWidth: 860 }}>
      <Typography
        sx={{
          fontSize: typography.caption,
          color: colors.mutedForeground,
          mb: 1.75,
        }}
      >
        Pick any insight to explore it · {dateLabel}
      </Typography>
      {Object.values(topics).map((t) => {
        const TopicIcon = t.icon;
        return (
          <ButtonBase
            key={t.key}
            onClick={() => {
              setInsightTopic(t.key);
              setInsightQ(null);
            }}
            sx={{
              display: 'block',
              width: '100%',
              textAlign: 'left',
              backgroundColor: `${t.color}14`,
              borderRadius: 3,
              p: 2,
              mb: 1.5,
              '&:hover': { backgroundColor: `${t.color}22` },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                gap: 1.5,
              }}
            >
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.625,
                  backgroundColor: t.color,
                  py: 0.375,
                  px: 1,
                  borderRadius: 999,
                  mb: 1,
                }}
              >
                <TopicIcon sx={{ fontSize: 13, color: '#fff' }} />
                <Typography
                  sx={{
                    color: '#fff',
                    fontSize: typography.caption,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: 1,
                  }}
                >
                  {t.tag}
                </Typography>
              </Box>
              <Typography
                sx={{
                  flexShrink: 0,
                  color: t.color,
                  fontSize: typography.caption,
                  fontWeight: 700,
                }}
              >
                5 questions →
              </Typography>
            </Box>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: typography.body,
                mb: 0.5,
                color: colors.foreground,
              }}
            >
              {t.title}
            </Typography>
            <Typography
              sx={{
                fontSize: typography.caption,
                color: colors.mutedForeground,
                lineHeight: 1.6,
              }}
            >
              {t.summary}
            </Typography>
          </ButtonBase>
        );
      })}
    </Box>
  );
}
